import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PaypalService {

  private baseUrl = environment.apiBaseUrl + '/payment/paypal';

  private accessToken: string | null = null;

  constructor(private http: HttpClient) { }

  async getAccessToken() {
    if (!this.accessToken) {
      const paypalObject: any = await firstValueFrom(await this.http.post<any>(this.baseUrl + '/token', {}));
      this.accessToken = paypalObject.access_token;
    };
    return this.accessToken;
  }

  async createProduct(body: any) {
    const token = this.accessToken || (await this.getAccessToken());

    return await firstValueFrom(
      this.http.post(`${environment.paypalBaseUrl}/v1/catalogs/products`, body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
    );
  }

  async createPlan(body: any) {
    const token = this.accessToken || (await this.getAccessToken());

    return await firstValueFrom(
      this.http.post(`${environment.paypalBaseUrl}/v1/billing/plans`, body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
    );
  }

  async getAllProducts(): Promise<any[]> {
    const token = this.accessToken || (await this.getAccessToken());

    const response: any = await firstValueFrom(
      this.http.get(
        `${environment.paypalBaseUrl}/v1/catalogs/products?page_size=100&total_required=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
    );
    console.log('PayPal Products API Response:', response);
    return Array.isArray(response.products) ? response.products : [];
  }

  async getPlansByProduct(productId: string): Promise<any[]> {
    const token = this.accessToken || (await this.getAccessToken());

    const response: any = await firstValueFrom(
      this.http.get(
        `${environment.paypalBaseUrl}/v1/billing/plans?product_id=${productId}&page_size=20`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
    );
    console.log(' PayPal Plans API Response:', response);
    return Array.isArray(response.plans) ? response.plans : [];
  }

  async getPlanDetails(planId: string): Promise<any> {
    const token = this.accessToken || (await this.getAccessToken());

    return firstValueFrom(
      this.http.get(`${environment.paypalBaseUrl}/v1/billing/plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    );
  }

  async createSubscription(planId: string) {
    const token = this.accessToken || (await this.getAccessToken());

    const body = {
      plan_id: planId,
      application_context: {
        brand_name: 'PayMe-dev',
        locale: 'en-US',
        user_action: 'SUBSCRIBE_NOW',
        shipping_preference: 'NO_SHIPPING',
        return_url: 'http://localhost:4200/success',
        cancel_url: 'http://localhost:4200/cancel',
      },
    };

    return await firstValueFrom(
      this.http.post(`${environment.paypalBaseUrl}/v1/billing/subscriptions`, body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
    );
  }

  // 7️⃣ Get Subscription Details
  async getSubscriptionDetails(subscriptionId: string) {
    const token = await this.getAccessToken();

    return await firstValueFrom(
      this.http.get(`${environment.paypalBaseUrl}/v1/billing/subscriptions/${subscriptionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
    );
  }

  /** Fetch one page of products */
  async fetchProductsPage(page = 1, pageSize = 20): Promise<any> {
    const accessToken = this.accessToken || (await this.getAccessToken());
    const headers = new HttpHeaders({ Authorization: `Bearer ${accessToken}` });
    const url = `${environment.paypalBaseUrl}/v1/catalogs/products?page_size=${pageSize}&page=${page}`;
    return await lastValueFrom(this.http.get<any>(url, { headers })).catch(err => { throw err; });
  }

  /** Deactivate (POST) plan */
  async deactivatePlanO(planId: string): Promise<void> {
    const accessToken = this.accessToken || (await this.getAccessToken());
    const headers = new HttpHeaders({ Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' });
    const url = `${environment.paypalBaseUrl}/v1/billing/plans/${planId}/deactivate`;
    await lastValueFrom(this.http.post(url, {}, { headers }));
  }

  /** Deactivate (POST) plan */
  async deactivatePlan(planId: string): Promise<void> {
    const accessToken = this.accessToken || (await this.getAccessToken());
    const headers = new HttpHeaders({ Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' });
    const url = `${environment.paypalBaseUrl}/v1/billing/plans/${planId}/deactivate`;
    await lastValueFrom(this.http.post(url, {}, { headers }));
  }
}
